import { makeAutoObservable } from 'mobx';

import { getShareBetCode, getShareBetData } from '@/lib/api/bets';
import { logger } from '@/lib/api/logger';
import { mobx } from '@/lib/mobx';
import { getFromWebSocket } from '@/helpers/getFromWebSocket';
import { betTrackMark } from '@/config/constants';

class ShareBet {
  constructor() {
    makeAutoObservable(this);
  }

  shareBetDataLength: number = 0;

  isShowShareBet: boolean = true;

  isShareBetEnterCode: boolean = false;

  shareBetCode: string = '';

  betBuilderOutcomeTotals: Map<string, number> = new Map();

  betBuilderProcessedOutcomes: Map<string, Set<string>> = new Map();

  shareBetSlipTab: BetSlipTab = null;

  private currentRestoreSessionId: string = '';

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  setIsShowShareBet = (bool: boolean = false): void => {
    this.isShowShareBet = bool;
  };

  setShareBetSlipTab = (tab: BetSlipTab = null) => {
    this.shareBetSlipTab = tab;
  };

  setIsShareBetEnterCode = (bool: boolean = false): void => {
    this.isShareBetEnterCode = bool;
  };

  getShareBet = async (code: string): Promise<void> => {
    try {
      const { data = {} } = (await getShareBetData(code)) as ApiResponse<ShareBetData>;
      const { code: responseCode = '', data: items = [], type = null } = data.data || {};
      if (responseCode !== code) {
        return;
      }

      if (items.length) {
        this.currentRestoreSessionId = this.generateSessionId();
        const shareBetSlipTab: BetSlipTab =
          type !== null ? (type === 'ordinar' ? 'SINGLEBET' : 'EXPRESS') : 'SINGLEBET';
        this.setShareBetSlipTab(shareBetSlipTab);
        this.betBuilderOutcomeTotals.clear();
        items.forEach((item) => {
          if (item.type === 'betbuilder') {
            this.betBuilderOutcomeTotals.set(item.eventId, item.outcomes.length);
          }
        });

        mobx.betslip.clearStorage();
        mobx.betslip.bets = [];
        mobx.betslip.setBlocksIdsMap({});
        this.shareBetCode = code;
        this.setIsShareBetEnterCode(true);
        this.shareBetDataLength = items.length;
        await mobx.betslip.restoreData(items);
      }
    } catch (error) {
      logger.error('Share bet invalid code', {
        error: error as Error,
      });
      mobx.ui.setAlert({
        text: 'SHAREBET.ERROR.INVALID_CODE',
        type: 'error',
      });
    }
  };

  shareBetCreateCode = async (
    betsData: ShareBetItem[] = [],
    tab: string = '',
  ): Promise<string | boolean> => {
    const { liveView = false } = mobx.ui || {};

    const isBetsData = !!betsData.length;
    const bets = isBetsData ? betsData : liveView ? [mobx.betslip.liveBet] : mobx.betslip.bets;
    if (!bets.length) {
      return false;
    }

    const currentTab = tab || mobx.betslip.currentTab;
    const type = currentTab === 'SINGLEBET' ? 'ordinar' : currentTab.toLowerCase() || 'ordinar';

    const shareBetData: ShareBetCreateCodeData = {
      items: bets.map((bet) => {
        const typedBet = bet as BetDetails;
        const betType = typedBet.isBetBuilder ? 'betbuilder' : 'bet';

        const outcomeId = isBetsData
          ? (typedBet.outcomeId ?? typedBet.id ?? '')
          : typedBet.id || '';

        const marketId = isBetsData
          ? (typedBet.eventMarket?.id ?? typedBet.pidLid?.split('.').splice(-1).toString() ?? '')
          : typedBet.pidLid?.split('.').splice(-1).toString() || '';

        const outcomes = typedBet.isBetBuilder
          ? ((typedBet.betBuilderOutcomes ?? typedBet.outcomes ?? []) as BetBuilderOutcomes).map(
              (o: BetBuilderOutcome) => ({
                marketId: (o.pidLid ?? o.marketId ?? '').split('.').pop() || '',
                outcomeId: o.outcomeId,
              }),
            )
          : [{ marketId, outcomeId }];

        const eventId = isBetsData
          ? (typedBet.event?.id ?? typedBet.eventId?.split('.').splice(-1).toString() ?? '')
          : typedBet.eventId?.split('.').splice(-1).toString() || '';

        return {
          eventId,
          outcomes,
          type: betType,
        };
      }),
      type,
    };

    try {
      const response = (await getShareBetCode(shareBetData)) as ApiResponse<{ code: string }>;
      const { data } = response || {};
      const { code = '' } = data?.data || {};
      return code;
    } catch (error) {
      logger.error('getShareBetCode error', {
        error: error as Error,
        shareBetData,
      });
      return false;
    }
  };

  shareBetGetCodeURL = async () => {
    const url = new URL(window.location.href);
    const shareBetCode = url.searchParams.get('shareBet');
    url.searchParams.delete('shareBet');
    history.replaceState({}, document.title, url.toString());
    if (shareBetCode) {
      await this.getShareBet(shareBetCode);
      return shareBetCode;
    }
    return false;
  };

  restoreShareBet = async ({
    isBetBuilder = false,
    item = {},
    outcome = {},
    marketName = '',
    marketLid = '',
  }) => {
    const {
      isBetBuilderSupported = false,
      eventId = '',
      name: outcomeName = '',
      id: outcomeId = '',
      pid = '',
    } = outcome as {
      isBetBuilderSupported?: boolean;
      eventId: string;
      name: string;
      id: string;
      pid: string;
    };

    await getFromWebSocket({
      entityName: 'event',
      ids: [eventId],
    });

    await getFromWebSocket({
      entityName: 'market',
      ids: [pid],
    });

    const { betBuilderOutcomes = [] } = (item as AwaitedItem) ?? { betBuilderOutcomes: [] };
    if (isBetBuilder) {
      const sessionEventKey = `${this.currentRestoreSessionId}-${eventId}`;
      if (!this.betBuilderProcessedOutcomes.has(sessionEventKey)) {
        this.betBuilderProcessedOutcomes.set(sessionEventKey, new Set());
      }

      let processedSet: Set<string> = new Set();
      if (
        Boolean(this.betBuilderProcessedOutcomes) &&
        this.betBuilderProcessedOutcomes.size > 0 &&
        this.betBuilderProcessedOutcomes.has(sessionEventKey) &&
        Boolean(this.betBuilderProcessedOutcomes.get(sessionEventKey))
      ) {
        processedSet = this.betBuilderProcessedOutcomes.get(sessionEventKey) as Set<string>;
        processedSet.add(outcomeId);
      }

      const pureEventId = eventId.split('.').pop() || '';
      const totalOutcomes =
        this.betBuilderOutcomeTotals.get(pureEventId) || betBuilderOutcomes.length;
      const processedCount = processedSet.size;
      const isLastOutcome = processedCount === totalOutcomes;

      const betBuilderData = {
        eventId,
        isBetBuilderSupported,
        isLast: isLastOutcome,
        marketLid,
        marketName,
        outcomeId,
        outcomeName,
        sessionId: this.currentRestoreSessionId,
      };

      await mobx.betBuilder.restoreShareBetBuilder({ betBuilderData, isShareBet: true });

      if (isLastOutcome) {
        this.betBuilderProcessedOutcomes.delete(sessionEventKey);
        const pureEventId = eventId ? eventId.split('.').pop() || '' : '';
        if (pureEventId && mobx.betBuilder?.betBuilderMarketsListCache) {
          delete mobx.betBuilder.betBuilderMarketsListCache[pureEventId];
        }
      }
    } else {
      mobx.betslip.add({
        marketId: marketLid,
        outcomeId: outcomeId as string,
        trackMarks: [betTrackMark.sharedBet, this.shareBetCode],
      });
    }
  };
}

export { ShareBet };

interface ShareBetOutcome {
  marketId: string;
  outcomeId: string;
}

interface ShareBetItem {
  type: 'betbuilder' | 'bet';
  eventId: string;
  outcomes: ShareBetOutcome[];
}

interface BetBuilderOutcome {
  marketId?: string;
  outcomeId: string;
  pidLid?: string;
}

type BetBuilderOutcomes = BetBuilderOutcome[];

interface ShareBetData {
  code: string;
  data: ShareBetItem[];
  type: string;
}

interface ShareBetCreateCodeData {
  items: ShareBetItem[];
  type: string;
}

interface ApiResponse<T = Record<string, unknown>> {
  data?: {
    data?: T;
  };
}

interface AwaitedItem {
  eventId: string;
  pidLid?: string;
  isBetBuilder?: boolean;
  betBuilderOutcomes?: BetBuilderOutcomes;
  id?: string;
  outcomeId?: string;
}

interface Market {
  lid: string;
  marketId?: string;
  outcomeId?: string;
  eventId?: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface BetDetails {
  eventId?: string;
  id?: string;
  outcomeId?: string;
  pidLid?: string;
  eventMarket?: { id?: string };
  event?: { id?: string };
  isBetBuilder?: boolean;
  betBuilderOutcomes?: BetBuilderOutcomes;
  outcomes?: BetBuilderOutcomes;
}

type BetSlipTab = 'EXPRESS' | 'SINGLEBET' | null;
